import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Award, Globe, Users, TrendingUp, CheckCircle, Target } from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';

const AboutPage = () => {
  const [currency, setCurrency] = useState('USD');

  const values = [
    {
      icon: CheckCircle,
      title: 'Quality Assurance',
      description: 'Every vehicle undergoes rigorous inspection before export. We ensure you receive exactly what you expect.'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We provide dedicated support throughout your buying journey.'
    },
    {
      icon: Award,
      title: 'Trust & Transparency',
      description: 'Honest pricing, detailed vehicle reports, and clear communication at every step.'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'Serving customers in 50+ countries with reliable worldwide shipping and logistics.'
    }
  ];

  const milestones = [
    { year: '2015', title: 'Company Founded', description: 'Started with a vision to connect global buyers with quality Japanese vehicles' },
    { year: '2017', title: 'Expanded Operations', description: 'Reached 1,000+ vehicle exports and expanded to 20 countries' },
    { year: '2019', title: 'Digital Transformation', description: 'Launched online platform for seamless customer experience' },
    { year: '2023', title: 'Global Leader', description: 'Serving 50+ countries with 7,000+ satisfied customers worldwide' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="About Us - Global Trade Car | Japanese Vehicle Exporters"
        description="Learn about Global Trade Car, your trusted partner for premium Japanese vehicles. Over 7,000 customers served in 50+ countries."
      />
      <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            About Global Trade Car
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your trusted partner in Japanese vehicle exports since 2015. Connecting quality vehicles with customers worldwide.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <Card>
            <CardContent className="p-8">
              <Target className="h-12 w-12 text-red-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed">
                To provide global customers with access to high-quality Japanese vehicles through transparent pricing, 
                reliable service, and exceptional customer support. We strive to make international vehicle purchasing 
                simple, secure, and satisfying.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-8">
              <TrendingUp className="h-12 w-12 text-red-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
              <p className="text-gray-600 leading-relaxed">
                To become the world's most trusted Japanese vehicle exporter, recognized for our commitment to quality, 
                innovation, and customer satisfaction. We aim to set industry standards in transparency and service excellence.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Our Story */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Our Story</h2>
          <Card>
            <CardContent className="p-8">
              <p className="text-gray-600 leading-relaxed mb-4">
                Global Trade Car was founded in 2015 with a simple mission: to bridge the gap between Japanese quality 
                vehicles and international buyers. What started as a small operation has grown into a trusted name in 
                the vehicle export industry.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Our founders recognized the global demand for reliable, well-maintained Japanese vehicles and saw an 
                opportunity to provide transparent, customer-focused service in an industry that often lacked it. 
                Today, we're proud to serve customers in over 50 countries, having facilitated the export of thousands 
                of vehicles.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Every vehicle we export represents our commitment to quality and customer satisfaction. Our team works 
                tirelessly to ensure each transaction is smooth, transparent, and exceeds expectations. We don't just 
                export vehicles – we build lasting relationships with our customers around the world.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Our Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                      <Icon className="h-8 w-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Our Journey</h2>
          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-red-200"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="flex-1">
                    <Card className={index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}>
                      <CardContent className="p-6">
                        <div className="text-2xl font-bold text-red-600 mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                        <p className="text-gray-600">{milestone.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="hidden md:block relative">
                    <div className="w-4 h-4 bg-red-600 rounded-full border-4 border-white shadow"></div>
                  </div>
                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-red-600 rounded-2xl p-12 mb-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-4xl font-bold mb-2">7,000+</div>
              <div className="text-red-100">Happy Customers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-red-100">Countries Served</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">10+</div>
              <div className="text-red-100">Vehicles in Stock</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">8+</div>
              <div className="text-red-100">Years of Experience</div>
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Global Trade Car?</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            We combine Japanese quality, transparent pricing, and exceptional service to deliver an unmatched 
            vehicle buying experience. Our commitment to customer satisfaction sets us apart in the industry.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/vehicles">
              <button className="px-8 py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold">
                Browse Our Inventory
              </button>
            </a>
            <a href="/contact">
              <button className="px-8 py-4 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-semibold">
                Contact Us
              </button>
            </a>
          </div>
        </div>
      </div>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default AboutPage;